import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import { GETAPI, POSTAPIFORMDATA } from "../../api/Api";
import { ApiConstants } from "../../constants/ApiConstants";
import { Routes } from "../../constants/RouteConstants";
import Spinner from "../../components/Spinner";
import { GetProfileAction } from "../../redux/Action/ProfileAction";
import { useDispatch, useSelector } from "react-redux";
import { saveToStorage } from "../../utils/Helper";
import { StorageConstants } from "../../constants/Constants";
import { Config } from "../../config/Config";
import SubscriptionBankSummary from "./SubscriptionBankSummary";
import { OfflineSubscriptionUserStatus } from "../../utils/subscriptionAccess";

const SubscriptionPurchase = () => {
  const [searchParams] = useSearchParams();
  const planId = searchParams.get("plan_id");
  const dispatch = useDispatch<any>();

  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState<any>(null);
  const [bank, setBank] = useState<any>(null);
  const [transactionId, setTransactionId] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [plansCtx, setPlansCtx] = useState<{
    salonSub: any;
    currentPlan: any;
    pendingRequest: any | null;
    hasPendingUpgrade: boolean;
  } | null>(null);

  const isOwner = Number(Config?.USER_DETAILS?.role_id) === 1;
  const profileData = useSelector((state: any) => state.GetProfileState);
  const profileUser = profileData?.data?.data || Config?.USER_DETAILS;
  const profileSuspended =
    Number(profileUser?.offline_subscription_status) ===
    OfflineSubscriptionUserStatus.SUSPENDED;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const plansRes: any = await GETAPI(ApiConstants.OfflineSubscriptionPlans, {}, false);
      const bankRes: any = await GETAPI(ApiConstants.OfflineSubscriptionBankDetails, {}, false);
      if (!cancelled && plansRes?.status === true && plansRes?.data) {
        const sub = plansRes.data.salon_subscription ?? null;
        const pend = plansRes.data.pending_request ?? null;
        const hp =
          plansRes.data.has_pending_upgrade === true ||
          (!!pend &&
            Number(sub?.offline_subscription_status) ===
              OfflineSubscriptionUserStatus.ACTIVE);
        setPlansCtx({
          salonSub: sub,
          currentPlan: plansRes.data.current_plan ?? null,
          pendingRequest: pend,
          hasPendingUpgrade: hp,
        });
      } else if (!cancelled) {
        setPlansCtx(null);
      }
      if (
        !cancelled &&
        plansRes?.status === true &&
        Array.isArray(plansRes?.data?.plans) &&
        planId
      ) {
        const pid = parseInt(String(planId), 10);
        const found = plansRes.data.plans.find((x: any) => x.id === pid);
        setPlan(found || null);
      }
      if (!cancelled && bankRes?.status === true && bankRes?.data) {
        setBank(bankRes.data);
      }
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [planId]);

  const isUpgradeFlow =
    plan &&
    plansCtx?.salonSub?.offline_subscription_status ===
      OfflineSubscriptionUserStatus.ACTIVE &&
    plansCtx?.currentPlan &&
    Number(plan.price) > Number(plansCtx.currentPlan.price);

  const isSuspended =
    plansCtx?.salonSub?.offline_subscription_status ===
      OfflineSubscriptionUserStatus.SUSPENDED || profileSuspended;

  const pendingVerify =
    plansCtx?.salonSub?.offline_subscription_status ===
    OfflineSubscriptionUserStatus.PENDING_VERIFY;
  const blockDuplicatePayment =
    pendingVerify || !!plansCtx?.hasPendingUpgrade;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSuspended || blockDuplicatePayment) return;
    if (!planId || !file || !transactionId.trim()) return;
    setSubmitting(true);
    const fd = new FormData();
    fd.append("plan_id", String(planId));
    fd.append("transaction_id", transactionId.trim());
    fd.append("payment_proof", file);
    const res: any = await POSTAPIFORMDATA(
      ApiConstants.OfflineSubscriptionSubmitPayment,
      fd,
      true,
    );
    setSubmitting(false);
    if (res?.status) {
      const profile: any = await GETAPI(ApiConstants.GetProfile, {}, false);
      if (profile?.status && profile?.data) {
        saveToStorage({ key: StorageConstants.USER_DETAIL, value: profile.data });
      }
      dispatch(GetProfileAction({}));
      window.location.href = Routes.SubscriptionPlans;
    }
  };

  if (!planId) {
    return (
      <div className="col-12 col-lg-9">
        <div className="py-5 px-3">
          <p>
            <FormattedMessage id="select_plan_first" defaultMessage="Please select a plan first." />
          </p>
          <Link to={Routes.SubscriptionPlans} className="btn btn-fill">
            <FormattedMessage id="subscription_plans_title" defaultMessage="Subscription plans" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="col-12 col-lg-9">
      <section className="innerBanner">
        <div className="BannerText container py-5">
          <h1>
            {isUpgradeFlow ? (
              <FormattedMessage
                id="upgrade_subscription_purchase"
                defaultMessage="Upgrade subscription"
              />
            ) : (
              <FormattedMessage
                id="complete_subscription_purchase"
                defaultMessage="Complete subscription purchase"
              />
            )}
          </h1>
        </div>
      </section>
      <section className="py-4 px-3 px-lg-4">
        {loading ? (
          <Spinner />
        ) : !plan ? (
          <p>
            <FormattedMessage id="plan_not_found" defaultMessage="Plan not found." />{" "}
            <Link to={Routes.SubscriptionPlans}>
              <FormattedMessage id="back" defaultMessage="Back" />
            </Link>
          </p>
        ) : (
          <div className="row gy-4 align-items-start">
            {isSuspended && (
              <div className="col-12">
                <div className="alert alert-danger" role="alert">
                  <FormattedMessage
                    id="subscription_suspended_no_payment"
                    defaultMessage="Your account is suspended. You cannot submit a new payment until an administrator reactivates your subscription. See subscription plans for details."
                  />
                </div>
              </div>
            )}
            <div className="col-12 col-lg-6">
              <h5>
                <FormattedMessage id="bank_transfer_details" defaultMessage="Bank transfer details" />
              </h5>
              <SubscriptionBankSummary bank={bank} />
            </div>
            <div className="col-12 col-lg-6">
              <h5>
                <FormattedMessage id="your_selection" defaultMessage="Your selection" />
              </h5>
              <p>
                <strong>{plan.name}</strong>
              </p>
              <p>
                <FormattedMessage id="amount" defaultMessage="Amount" />:{" "}
                <strong>{Number(plan.price).toFixed(2)}</strong>
              </p>
              <p>
                <FormattedMessage id="validity" defaultMessage="Validity" />: {plan.validity_days}{" "}
                <FormattedMessage id="days" defaultMessage="days" />
              </p>
              {!isOwner ? (
                <div className="alert alert-secondary mt-3" role="alert">
                  <FormattedMessage
                    id="subscription_purchase_owner_only"
                    defaultMessage="Only the salon owner can submit payment for a subscription. Please ask the owner to complete this step."
                  />
                </div>
              ) : isSuspended ? (
                <p className="small text-muted mt-3 mb-0">
                  <Link to={Routes.SubscriptionPlans} className="btn btn-fill btn-sm">
                    <FormattedMessage id="subscription_plans_title" defaultMessage="Subscription plans" />
                  </Link>
                </p>
              ) : blockDuplicatePayment ? (
                <div className="alert alert-info mt-3" role="alert">
                  {plansCtx?.hasPendingUpgrade ? (
                    <FormattedMessage
                      id="subscription_purchase_blocked_upgrade_pending"
                      defaultMessage="You already have an upgrade payment awaiting admin review. Your current plan stays active. You can check status on the subscription plans page."
                    />
                  ) : (
                    <FormattedMessage
                      id="subscription_purchase_blocked_pending_verify"
                      defaultMessage="A payment is already pending verification. Please wait for admin approval before submitting again."
                    />
                  )}
                </div>
              ) : (
                <>
                  <hr />
                  <h6>
                    <FormattedMessage id="payment_proof_section" defaultMessage="After payment" />
                  </h6>
                  <p className="small text-muted">
                    <FormattedMessage
                      id="payment_outside_note"
                      defaultMessage="Complete the transfer in your bank app or website, then enter the transaction ID and upload your receipt (PNG or JPEG)."
                    />
                  </p>
                  <form onSubmit={onSubmit}>
                    <div className="mb-3">
                      <label className="form-label">
                        <FormattedMessage id="transaction_id" defaultMessage="Transaction ID" />
                      </label>
                      <input
                        className="form-control"
                        value={transactionId}
                        onChange={(e) => setTransactionId(e.target.value)}
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">
                        <FormattedMessage id="payment_proof" defaultMessage="Payment proof" />
                      </label>
                      <input
                        type="file"
                        className="form-control"
                        accept="image/png,image/jpeg,image/jpg"
                        onChange={(e) => setFile(e.target.files?.[0] || null)}
                        required
                      />
                    </div>
                    <button type="submit" className="btn btn-fill" disabled={submitting}>
                      {submitting ? (
                        <FormattedMessage id="submitting" defaultMessage="Submitting…" />
                      ) : (
                        <FormattedMessage id="submit_for_verification" defaultMessage="Submit for verification" />
                      )}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        )}
        <div className="mt-4">
          <Link to={Routes.SubscriptionPlans} className="btn btn-outline-secondary">
            <FormattedMessage id="back" defaultMessage="Back" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default SubscriptionPurchase;
